import { defineStore } from "pinia";
import { ref } from "vue";
import { listSprints } from "@/api/sprints";
import type { ApiError, SprintListItem } from "@/api/types";
import { useApi } from "@/composables/useApi";
import {
  buildNarrative,
  cadenceSummary,
  deriveProjectHealth,
  metricSpark,
  scanRisks,
  HEADLINE_METRIC_IDS,
} from "@/lib/insights";

interface ProjectEntry {
  items: SprintListItem[];
  loadedAt: number;
  error: ApiError | null;
}

type BoardType = "scrum" | "kanban";

// A row with no board_type is a Scrum sprint. Centralised here so the filter
// matches the read-API's reshape default.
function matchesBoard(s: SprintListItem, bt?: BoardType): boolean {
  if (!bt) return true;
  return (s.board_type ?? "scrum") === bt;
}

export const useSprintsStore = defineStore("sprints", () => {
  const byProject = ref<Record<string, ProjectEntry>>({});
  const loadingKey = ref<string | null>(null);

  function entry(key: string): ProjectEntry {
    return byProject.value[key] ?? { items: [], loadedAt: 0, error: null };
  }

  async function load(projectKey: string, force = false): Promise<void> {
    const cur = entry(projectKey);
    if (!force && Date.now() - cur.loadedAt < 60_000 && cur.items.length > 0) return;
    const { client } = useApi();
    loadingKey.value = projectKey;
    try {
      const items = await listSprints(client, projectKey);
      byProject.value = {
        ...byProject.value,
        [projectKey]: { items, loadedAt: Date.now(), error: null },
      };
    } catch (err) {
      byProject.value = {
        ...byProject.value,
        [projectKey]: { items: cur.items, loadedAt: cur.loadedAt, error: err as ApiError },
      };
    } finally {
      if (loadingKey.value === projectKey) loadingKey.value = null;
    }
  }

  // `boardType` scopes a project's rows to one board (Scrum vs Kanban). Filtering
  // preserves the API's newest-first order, so every downstream aggregator
  // (cadence count, sprints[0] reads) becomes board-scoped with no change to the
  // lib functions. Omit it to get the full mixed list.
  function items(projectKey: string, boardType?: BoardType): SprintListItem[] {
    const all = entry(projectKey).items;
    return boardType ? all.filter((s) => matchesBoard(s, boardType)) : all;
  }
  function error(projectKey: string): ApiError | null {
    return entry(projectKey).error;
  }
  function loading(projectKey: string): boolean {
    return loadingKey.value === projectKey;
  }
  function latest(projectKey: string, boardType?: BoardType): SprintListItem | undefined {
    return items(projectKey, boardType)[0];
  }

  function insightsFor(projectKey: string, boardType?: BoardType) {
    const list = items(projectKey, boardType);
    const risks = scanRisks(list);
    return {
      health: deriveProjectHealth(list),
      cadence: cadenceSummary(list),
      risks,
      narrative: buildNarrative(list, risks),
      headlines: HEADLINE_METRIC_IDS.map((id) => metricSpark(list, id, 6)),
    };
  }

  return {
    // raw state (reactive ref kept for tests; views use the helpers below)
    byProject,
    loadingKey,
    // accessors — plain functions; reactivity comes from byProject ref reads inside.
    items,
    error,
    loading,
    latest,
    insightsFor,
    // actions
    load,
  };
});
